// Figma: https://www.figma.com/design/iYlbYhvyJjh7X00UAcqCWt/About-page?node-id=13-1111
import { Box } from "@chakra-ui/react";
import { useEffect, useRef, useCallback } from "react";
import { aboutAsset } from "../aboutAssets";

const aboutText = aboutAsset("about-text.svg");

// ── Timing ─────────────────────────────────────────────────────────────────
const ANIMATION_DELAY = 200; // ms before first stroke begins
const DEBUG_TIME_SCALE = 1; // >1 = slower animation for debugging (set to 1 for production)

// ── Skeleton stroke paths ──────────────────────────────────────────────────
// Hand-traced center-lines through each letter's brush strokes.
// Rendered as thick white strokes inside an SVG <mask>; as strokeDashoffset
// animates from pathLength to 0, the filled art is progressively revealed
// along the actual brush path — creating a true "being drawn" effect.
//
// SVG viewBox: 0 0 141 73
// Letter bounding boxes from path analysis:
//   A: x[7,59]  y[10,72]   b: x[46,77] y[11,62]
//   o: x[68,100] y[13,53]  u: x[92,120] y[19,51]  t: x[112,135] y[3,57]

interface LetterStroke {
  name: string;
  d: string;
  width: number;
  delay: number; // ms relative to ANIMATION_DELAY
  dur: number;   // ms
}

const STROKES: LetterStroke[] = [
  // ── A ── main stroke: bottom-left → up to peak → loop → crossbar
  {
    name: "A-1",
    d: "M6.15 73.39C15.32 67.39 35.95 49.69 49.15 20.89C53.65 11.07 39.22 13.39 28.15 28.89C23.15 35.89 17.65 47.89 21.65 52.89C24.85 56.89 42.98 47.22 51.65 41.89",
    width: 6,
    delay: 0,
    dur: 220,
  },
  // ── A ── right leg descending stroke
  {
    name: "A-2",
    d: "M57.99 10.13C48.65 26.13 33.19 60.33 45.99 69.13",
    width: 6,
    delay: 230,
    dur: 150,
  },
  // ── b ── stem: top → down
  {
    name: "b-1",
    d: "M70.25 11.90C64.75 20.23 53.65 42.40 53.25 64.40",
    width: 6,
    delay: 400,
    dur: 180,
  },
  // ── b ── belly curve
  {
    name: "b-2",
    d: "M58.23 54.03C60.06 47.86 65.44 33.54 74.23 31.03C77.73 30.03 69.23 53.73 65.73 53.03C63.23 52.53 62.73 50.03 62.73 48.03",
    width: 6,
    delay: 600,
    dur: 180,
  },
  // ── o ── main oval loop
  {
    name: "o-1",
    d: "M86.68 31.20C86.34 29.86 85.57 28.16 83.68 31.20C81.18 35.20 78.67 40.70 78.17 47.70C77.49 57.22 84.67 49.20 87.67 38.20",
    width: 6,
    delay: 800,
    dur: 180,
  },
  // ── o ── tail/connector
  {
    name: "o-2",
    d: "M86.13 35.55C88.63 35.55 94.13 35.25 96.13 34.05",
    width: 6,
    delay: 1000,
    dur: 110,
  },
  // ── u ── down → curve → back up
  {
    name: "u-1",
    d: "M101.00 27.39C98.67 31.22 93.50 40.67 93.50 47.89C93.50 51.39 98.67 46.55 101.50 41.38",
    width: 6,
    delay: 1130,
    dur: 180,
  },
  // ── u ── second stroke
  {
    name: "u-2",
    d: "M111.11 24.61C107.94 30.45 103.11 43.11 109.11 47.11",
    width: 6,
    delay: 1330,
    dur: 150,
  },
  // ── t ── stem: top → down with flourish
  {
    name: "t-1",
    d: "M130.21 2.86C124.21 13.86 116.45 31.76 115.21 42.36C113.21 59.36 126.21 41.86 130.21 31.86",
    width: 6,
    delay: 1500,
    dur: 180,
  },
  // ── t ── crossbar
  {
    name: "t-2",
    d: "M113.15 21.50C115.82 20.66 124.25 18.70 136.65 17.50",
    width: 6,
    delay: 1700,
    dur: 110,
  },
];

const TOTAL_WRITE_DURATION =
  STROKES[STROKES.length - 1].delay + STROKES[STROKES.length - 1].dur;

// ── Easing helper ──────────────────────────────────────────────────────────
function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 2);
}

// ── Particle system ────────────────────────────────────────────────────────
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  opacity: number;
}

// ── Debug toggle ───────────────────────────────────────────────────────────
// Set to true to see the skeleton stroke paths drawn in green/cyan on screen.
// This shows exactly how the pen traces each letter.
const DEBUG_SHOW_STROKES = false;

// ── Unified animation loop ─────────────────────────────────────────────────
// Drives both the SVG stroke mask AND the particle canvas in a single rAF.
function useWriteAnimation(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  textRef: React.RefObject<HTMLDivElement | null>,
  pathRefs: React.MutableRefObject<(SVGPathElement | null)[]>,
  pathLengths: React.MutableRefObject<number[]>,
  debugPathRefs: React.MutableRefObject<(SVGPathElement | null)[]>,
) {
  const particles = useRef<Particle[]>([]);
  const penLiftBursts = useRef<Set<string>>(new Set());
  const animFrame = useRef<number>(0);
  const startTime = useRef<number>(0);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const textEl = textRef.current;
    if (!canvas || !textEl) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (canvas.width !== window.innerWidth || canvas.height !== window.innerHeight) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    const elapsed = (performance.now() - startTime.current) / DEBUG_TIME_SCALE;
    const textRect = textEl.getBoundingClientRect();

    // ── Update stroke dashoffsets (drives the mask reveal) ──
    const svgScaleX = textRect.width / 141;
    const svgScaleY = textRect.height / 73;

    for (let i = 0; i < STROKES.length; i++) {
      const el = pathRefs.current[i];
      const debugEl = debugPathRefs.current[i];
      const totalLen = pathLengths.current[i];
      if (!el || !totalLen) continue;

      const stroke = STROKES[i];
      const start = ANIMATION_DELAY + stroke.delay;
      const end = start + stroke.dur;

      let offsetValue: string;

      if (elapsed < start) {
        offsetValue = String(totalLen);
      } else if (elapsed >= end) {
        offsetValue = "0";

        // Spawn pen-lift burst only once per stroke, using endpoint from path d
        const burstKey = `burst-${i}`;
        if (!penLiftBursts.current.has(burstKey)) {
          penLiftBursts.current.add(burstKey);
          // Parse last two numbers from the d string as endpoint
          const nums = stroke.d.match(/-?[\d.]+/g);
          if (nums && nums.length >= 2) {
            const endX = parseFloat(nums[nums.length - 2]);
            const endY = parseFloat(nums[nums.length - 1]);
            const px = textRect.left + endX * svgScaleX;
            const py = textRect.top + endY * svgScaleY;
            for (let j = 0; j < 8; j++) {
              particles.current.push({
                x: px + (Math.random() - 0.5) * 4,
                y: py + (Math.random() - 0.5) * 4,
                vx: 0.15 + Math.random() * 0.3,
                vy: -(0.1 + Math.random() * 0.3),
                life: 0,
                maxLife: 50 + Math.random() * 70,
                size: 0.4 + Math.random() * 0.6,
                opacity: 0.25 + Math.random() * 0.3,
              });
            }
          }
        }
      } else {
        const progress = (elapsed - start) / stroke.dur;
        const eased = easeOut(progress);
        offsetValue = String(totalLen * (1 - eased));
      }

      // Apply to mask path
      el.style.strokeDashoffset = offsetValue;
      // Apply to debug visible path (if present)
      if (debugEl) {
        debugEl.style.strokeDasharray = String(totalLen);
        debugEl.style.strokeDashoffset = offsetValue;
      }
    }

    // ── Particle system ──
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particles.current = particles.current.filter((p) => {
      p.life++;
      if (p.life >= p.maxLife) return false;

      p.x += p.vx;
      p.y += p.vy;
      p.vy += (Math.random() - 0.5) * 0.01;
      p.vx *= 0.99;
      p.vy *= 0.99;

      const lifeRatio = p.life / p.maxLife;
      const alpha = (1 - lifeRatio) * p.opacity;

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 156, 128, ${alpha})`;
      ctx.fill();

      return true;
    });

    const allDone = elapsed > ANIMATION_DELAY + TOTAL_WRITE_DURATION;
    if (!allDone || particles.current.length > 0) {
      animFrame.current = requestAnimationFrame(animate);
    }
  }, [canvasRef, textRef, pathRefs, pathLengths, debugPathRefs]);

  useEffect(() => {
    // Measure all path lengths before starting animation
    pathRefs.current.forEach((el, i) => {
      if (el) {
        const len = el.getTotalLength();
        pathLengths.current[i] = len;
        el.style.strokeDasharray = String(len);
        el.style.strokeDashoffset = String(len);
      }
    });
    // Also init debug paths
    debugPathRefs.current.forEach((el, i) => {
      if (el) {
        const len = pathLengths.current[i] || el.getTotalLength();
        el.style.strokeDasharray = String(len);
        el.style.strokeDashoffset = String(len);
      }
    });

    startTime.current = performance.now();
    animFrame.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrame.current);
  }, [animate, pathRefs, pathLengths, debugPathRefs]);
}

// Debug stroke colors — each letter gets a distinct color
const DEBUG_COLORS = ["#00ff00", "#00ccff", "#ff00ff", "#ffff00", "#ff8800", "#ff0088"];

// ── Component ──────────────────────────────────────────────────────────────
export function AnimatedAboutText() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const debugPathRefs = useRef<(SVGPathElement | null)[]>([]);
  const pathLengths = useRef<number[]>([]);

  useWriteAnimation(canvasRef, textRef, pathRefs, pathLengths, debugPathRefs);

  return (
    <>
      {/* Full-viewport particle canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      />
      {/* About text with stroke-drawing mask animation */}
      <Box
        ref={textRef}
        w={{ base: "116px", md: "141px" }}
        h={{ base: "60px", md: "73px" }}
        position="absolute"
        top={{ base: "-50px", md: "-50px" }}
        left="50%"
        transform="translateX(-50%)"
      >
        <svg
          viewBox="0 0 141 73"
          width="100%"
          height="100%"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ overflow: "visible" }}
        >
          <defs>
            <mask id="about-write-mask">
              {/* Black = hidden, white strokes = revealed */}
              <rect width="141" height="73" fill="black" />
              {STROKES.map((stroke, i) => (
                <path
                  key={stroke.name}
                  ref={(el) => {
                    pathRefs.current[i] = el;
                  }}
                  d={stroke.d}
                  stroke="white"
                  strokeWidth={stroke.width}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              ))}
            </mask>
          </defs>
          {/* The actual filled SVG art, revealed by the animated mask */}
          <image
            href={aboutText}
            width="141"
            height="73"
            mask="url(#about-write-mask)"
          />
          {/* Debug: visible skeleton strokes on top of the art */}
          {DEBUG_SHOW_STROKES &&
            STROKES.map((stroke, i) => (
              <path
                key={`debug-${stroke.name}`}
                ref={(el) => {
                  debugPathRefs.current[i] = el;
                }}
                d={stroke.d}
                stroke={DEBUG_COLORS[i % DEBUG_COLORS.length]}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                opacity={0.9}
              />
            ))}
          {/* Debug: also show the mask coverage area as semi-transparent */}
          {DEBUG_SHOW_STROKES &&
            STROKES.map((stroke, i) => (
              <path
                key={`debug-area-${stroke.name}`}
                d={stroke.d}
                stroke={DEBUG_COLORS[i % DEBUG_COLORS.length]}
                strokeWidth={stroke.width}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                opacity={0.12}
              />
            ))}
        </svg>
      </Box>
    </>
  );
}
